import { useQuery } from '@apollo/client/react'

import {
  Button,
  ChevronDownIcon,
  Columns3Icon,
  Combobox,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { selectCurrentOrganizationId } from '~publish/legacy/organizations/selectors'
import { useAppSelector } from '~publish/legacy/store'

import type { JSX } from 'react'

type IdeaGroupSelectorProps = {
  selectedGroupId?: string
  onGroupSelect?: (groupId: string | undefined) => void
  disabled?: boolean
}

export const getIdeaGroupsQuery = graphql(/* GraphQL */ `
  query GetIdeaGroups($organizationId: ID!) {
    ideaGroups(input: { organizationId: $organizationId }) {
      id
      name
      isLocked
    }
  }
`)

const UNASSIGNED_ITEM_ID = 'unassigned'

// This is a temporary component to select idea group
export const IdeaGroupSelector = ({
  selectedGroupId,
  onGroupSelect,
  disabled = false,
}: IdeaGroupSelectorProps): JSX.Element | null => {
  const organizationId: string = useAppSelector(selectCurrentOrganizationId)
  // TODO: this loading should happen in parent component, this is a temporary solution
  const { data, loading } = useQuery(getIdeaGroupsQuery, {
    skip: !organizationId,
    variables: { organizationId },
  })

  if (loading || !data) {
    return null
  }

  const handleGroupSelect = (groupId: string | undefined): void => {
    // Re-selecting the active item clears the Combobox value. Keep the
    // current group in that case; the Unassigned item is the explicit
    // way to clear the group.
    if (groupId === undefined) {
      return
    }
    onGroupSelect?.(groupId === UNASSIGNED_ITEM_ID ? undefined : groupId)
  }

  const selectedGroup = data.ideaGroups.find(
    (group) => group.id === selectedGroupId,
  )

  return (
    <Combobox
      multiple={false}
      value={selectedGroupId || UNASSIGNED_ITEM_ID}
      onChange={handleGroupSelect}
    >
      <Combobox.Trigger>
        <Button variant="secondary" disabled={disabled}>
          <Columns3Icon />
          {selectedGroup ? selectedGroup.name : 'Unassigned'}
          <ChevronDownIcon />
        </Button>
      </Combobox.Trigger>
      <Combobox.Content>
        <Combobox.Input placeholder="Search groups" />
        <Combobox.List>
          <Combobox.Empty>No groups found</Combobox.Empty>
          <Combobox.Item value={UNASSIGNED_ITEM_ID}>Unassigned</Combobox.Item>
          {data.ideaGroups.map((group) => (
            <Combobox.Item
              key={group.id}
              value={group.id}
              keywords={[group.name]}
              disabled={!!group.isLocked}
            >
              {group.name}
            </Combobox.Item>
          ))}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  )
}
